import { memo, useCallback } from 'react';
import { Config } from '@bonanza-social/toolkit/config';
import {
  useStyle,
  WebView,
  type ShouldStartLoadRequest,
  IS_ANDROID,
} from '@bonanza-social/moonlight';
import { type GameplayProps } from './Gameplay.types';
import { PixelRatio } from 'react-native';
import useGameplay from '../../hooks/useGameplay';
import useIosGameSoundResume from '../../hooks/useIosGameSoundResume';

function Gameplay({ id, deviceType, currencyCode, onExit }: GameplayProps) {
  const { url } = useGameplay(id, deviceType, currencyCode);
  const style = useStyle({ backgroundColor: 'screenBackground' });
  const {
    injectedJavaScriptBeforeContentLoadedForMainFrameOnly,
    iosGameSoundResumeScript,
    webViewRef,
  } = useIosGameSoundResume();

  const onShouldStartLoadWithRequest = useCallback(
    (request: ShouldStartLoadRequest) => {
      const currentUrl = request.url;
      const mainUrl = request.mainDocumentURL;

      const isPlatformUrl =
        currentUrl?.startsWith(Config.PLATFORM_URL) ||
        mainUrl?.startsWith(Config.PLATFORM_URL);

      if (isPlatformUrl) {
        onExit?.();
        return false;
      }

      return true;
    },
    [onExit],
  );

  const fontScale = PixelRatio.getFontScale();
  const textZoomRestriction = Math.min(fontScale * 100, 100); // Limit text zoom to 100%
  const androidTextZoom = IS_ANDROID ? textZoomRestriction : undefined;

  return url ? (
    <WebView
      ref={webViewRef}
      allowFileAccess
      scrollEnabled
      nestedScrollEnabled
      allowsBackForwardNavigationGestures
      domStorageEnabled
      javaScriptEnabled
      cacheEnabled={!__DEV__}
      webviewDebuggingEnabled={__DEV__}
      originWhitelist={['*']}
      source={{ uri: url }}
      style={style}
      allowsInlineMediaPlayback
      allowsFullscreenVideo={false}
      onShouldStartLoadWithRequest={onShouldStartLoadWithRequest}
      injectedJavaScriptBeforeContentLoadedForMainFrameOnly={
        injectedJavaScriptBeforeContentLoadedForMainFrameOnly
      }
      injectedJavaScriptBeforeContentLoaded={iosGameSoundResumeScript}
      testID="gameplay-iframe"
      textZoom={androidTextZoom}
    />
  ) : null;
}

export default memo(Gameplay);
